import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectors,
  ASSET_CLASSES,
  RISK_WEIGHTED_APPROACH,
  updateLeverageSettings,
  updateAssetClassOptions,
  AssetClassOptions,
  MarginOptions,
  CREATING,
} from 'state';
import { EditableTable } from 'reusable';
import { LoadingScreen, PercentInput } from 'components';
import { Button, InputNumber, Popover, Table, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

type State = {
  [type: string]: MarginOptions;
};
const LeverageSettings: React.FC = () => {
  const dispatch = useDispatch();
  const { asset_class_options, risk_approach } = useSelector(
    selectors.activeSession,
  );
  const session = useSelector(selectors.activeSession);
  const marginSettingsLocked = session.status !== CREATING;
  const [editing, setEditing] = useState(false);
  const [editingFutures, setEditingFutures] = useState(false);
  const [margins, setMargins] = useState<State>({});
  const [errors, setErrors] = useState<string[]>([]);
  // Futures maintenance threshold, displayed as a percentage (stored as a 0-1 fraction on the session)
  const [maintenanceThreshold, setMaintenanceThreshold] = useState(
    session.futures_maintenance_threshold * 100,
  );
  // Futures rollover: term in periods (0 = off) and fee shown as a percentage (stored as a 0-1 fraction)
  const [rolloverPeriods, setRolloverPeriods] = useState(
    session.futures_rollover_periods,
  );
  const [rolloverFeePct, setRolloverFeePct] = useState(
    session.futures_rollover_fee_pct * 100,
  );

  useEffect(() => {
    const newMargins = {
      FxForwards: { type: 'FxForwards', margin: session.fx_leverage },
      Bonds: { type: 'Bonds', margin: session.bond_leverage },
      Equity: { type: 'Equity', margin: session.equity_leverage },
      ETFEquities: {
        type: 'ETFEquities',
        margin: session.etf_equities_leverage,
      },
      ETFCommodities: {
        type: 'ETFCommodities',
        margin: session.etf_commodities_leverage,
      },
      Futures: { type: 'Futures', margin: session.futures_leverage },
    } as State;

    setMargins(newMargins);
  }, [
    session.fx_leverage,
    session.bond_leverage,
    session.equity_leverage,
    session.etf_equities_leverage,
    session.etf_commodities_leverage,
    session.futures_leverage,
  ]);

  useEffect(() => {
    setMaintenanceThreshold(session.futures_maintenance_threshold * 100);
  }, [session.futures_maintenance_threshold]);

  useEffect(() => {
    setRolloverPeriods(session.futures_rollover_periods);
  }, [session.futures_rollover_periods]);

  useEffect(() => {
    setRolloverFeePct(session.futures_rollover_fee_pct * 100);
  }, [session.futures_rollover_fee_pct]);

  useEffect(() => {
    if (!marginSettingsLocked) {
      return;
    }
    setEditing(false);
    setEditingFutures(false);
  }, [marginSettingsLocked]);

  if (margins === undefined) {
    return <LoadingScreen />;
  }

  const dataSource = [
    {
      type: 'FxForwards',
      margin: session.fx_leverage * 100,
    },
    {
      type: 'Bonds',
      margin: session.bond_leverage * 100,
    },
    {
      type: 'Equity',
      margin: session.equity_leverage * 100,
    },
    {
      type: 'ETFEquities',
      margin: session.etf_equities_leverage * 100,
    },
    {
      type: 'ETFCommodities',
      margin: session.etf_commodities_leverage * 100,
    },
    {
      type: 'Futures',
      margin: session.futures_leverage * 100,
    },
  ];

  // The session's persisted margins, in the shape updateLeverageSettings expects.
  // Used both to reset the table and as the (no-op) margins payload of the futures save.
  const buildSessionMargins = (): State => ({
    FxForwards: { type: 'FxForwards', margin: session.fx_leverage },
    Bonds: { type: 'Bonds', margin: session.bond_leverage },
    Equity: { type: 'Equity', margin: session.equity_leverage },
    ETFEquities: {
      type: 'ETFEquities',
      margin: session.etf_equities_leverage,
    },
    ETFCommodities: {
      type: 'ETFCommodities',
      margin: session.etf_commodities_leverage,
    },
    Futures: { type: 'Futures', margin: session.futures_leverage },
  });

  const reset = () => {
    setMargins(buildSessionMargins());
    setErrors([]);
  };

  const getProps = (field: 'margin') => {
    return {
      render(_: unknown, record: (typeof dataSource)[0]) {
        if (editing) {
          // const assetClass = record.asset_class;
          const allocation = margins[record.type];
          const margin = allocation?.[field] ?? 0;

          return (
            <div>
              <InputNumber
                value={margin}
                style={{ width: 70 }}
                onChange={value => {
                  if (value === undefined) {
                    return;
                  }
                  setMargins({
                    ...margins,
                    [record.type]: {
                      ...allocation,
                      [field]: value,
                    },
                  });
                }}
                step={0.01}
                min={0.01}
                max={1}
                formatter={value => {
                  const nr = Number(value) || 0;
                  return `${Math.round(nr * 100)}%`;
                }}
                parser={value => {
                  const parsedValue = `${value?.replaceAll('%', '')}`;
                  const nr = Number(parsedValue);
                  if (!isNaN(nr)) {
                    return Number((nr / 100).toFixed(2));
                  }
                  return 0;
                }}
              />
            </div>
          );
        }

        if (!record.margin) {
          return <div>-</div>;
        }
        return <div>{Math.round(record.margin)}%</div>;
      },
    };
  };

  const editButton = (
    <div
      style={{ position: 'relative', display: 'flex', gap: '10px', height: 30 }}
    >
      {!editing && (
        <Tooltip
          title={
            marginSettingsLocked
              ? 'Cannot modify margin settings after session has been started'
              : ''
          }
        >
          <span style={{ position: 'absolute', right: 0 }}>
            <Button
              disabled={marginSettingsLocked}
              onClick={() => {
                setEditing(true);
              }}
            >
              Edit
            </Button>
          </span>
        </Tooltip>
      )}
      {editing && (
        <>
          <Button
            type="primary"
            onClick={() => {
              if (!isValid()) {
                return;
              }

              setEditing(false);
              dispatch(updateLeverageSettings({ margins: margins }));
            }}
            style={{ position: 'absolute', right: 90 }}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              setEditing(false);
              reset();
            }}
            style={{ position: 'absolute', right: 0 }}
          >
            Cancel
          </Button>
        </>
      )}
    </div>
  );

  const isValid = () => {
    const errorsList = [];

    const valid = Object.values(margins).reduce((acc, { margin }) => {
      return acc && margin >= 0 && margin <= 100;
    }, true);

    if (!valid) {
      errorsList.push('Limits must be between 0 and 100');
    }

    if (errorsList.length) {
      setErrors(errorsList);
      return false;
    }

    return true;
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
    },
    {
      title: 'Percentage',
      ...getProps('margin'),
    },
  ];

  // Futures contract mechanics, edited independently of the margin table above.
  const futuresDataSource = [
    {
      key: 'maintenance',
      label: 'Maintenance threshold',
      value: maintenanceThreshold,
      onChange: (value: number | string | null | undefined) =>
        setMaintenanceThreshold(Number(value) || 0),
      min: 0,
      max: 100 as number | undefined,
      step: 1,
      unit: '%',
      tooltip:
        'A futures position is auto-liquidated once its remaining equity (margin + PnL) falls to or below the maintenance percentage of the posted margin. 0% = liquidate only when margin is fully wiped.',
    },
    {
      key: 'term',
      label: 'Contracts term',
      value: rolloverPeriods,
      onChange: (value: number | string | null | undefined) =>
        setRolloverPeriods(Math.max(0, Math.round(Number(value) || 0))),
      min: 0,
      max: undefined,
      step: 1,
      unit: 'periods',
      tooltip:
        'Every N periods the futures contract expires and is automatically rolled to the next contract, charging the rollover fee on each open position. 0 = rollover disabled.',
    },
    {
      key: 'fee',
      label: 'Rollover fee',
      value: rolloverFeePct,
      onChange: (value: number | string | null | undefined) =>
        setRolloverFeePct(Number(value) || 0),
      min: 0,
      max: 100 as number | undefined,
      step: 0.1,
      unit: '%',
      tooltip:
        "Charged on each open futures position at every rollover, as a percentage of the position's notional. If a player can't cover the fee, that position is liquidated instead.",
    },
  ];

  const futuresColumns = [
    {
      title: 'Setting',
      render: (_: unknown, record: (typeof futuresDataSource)[0]) => (
        <span>
          {record.label}
          <Tooltip title={record.tooltip}>
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </span>
      ),
    },
    {
      title: 'Value',
      render: (_: unknown, record: (typeof futuresDataSource)[0]) => (
        <span>
          <InputNumber
            value={record.value}
            disabled={!editingFutures}
            min={record.min}
            max={record.max}
            step={record.step}
            onChange={record.onChange}
            style={{ width: 70 }}
          />
          <span style={{ marginLeft: 4 }}>{record.unit}</span>
        </span>
      ),
    },
  ];

  const futuresEditButton = (
    <div
      style={{ position: 'relative', display: 'flex', gap: '10px', height: 30 }}
    >
      {!editingFutures && (
        <Tooltip
          title={
            marginSettingsLocked
              ? 'Cannot modify futures settings after session has been started'
              : ''
          }
        >
          <span style={{ position: 'absolute', right: 0 }}>
            <Button
              disabled={marginSettingsLocked}
              onClick={() => {
                setEditingFutures(true);
              }}
            >
              Edit
            </Button>
          </span>
        </Tooltip>
      )}
      {editingFutures && (
        <>
          <Button
            type="primary"
            onClick={() => {
              setEditingFutures(false);
              dispatch(
                updateLeverageSettings({
                  margins: buildSessionMargins(),
                  futures_maintenance_threshold: maintenanceThreshold / 100,
                  futures_rollover_periods: rolloverPeriods,
                  futures_rollover_fee_pct: rolloverFeePct / 100,
                }),
              );
            }}
            style={{ position: 'absolute', right: 90 }}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              setEditingFutures(false);
              setMaintenanceThreshold(
                session.futures_maintenance_threshold * 100,
              );
              setRolloverPeriods(session.futures_rollover_periods);
              setRolloverFeePct(session.futures_rollover_fee_pct * 100);
            }}
            style={{ position: 'absolute', right: 0 }}
          >
            Cancel
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Typography.Title level={4} style={{ display: 'inline-block' }}>
          Margin
        </Typography.Title>
      </div>

      <Table
        rowKey="margins"
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        footer={() => editButton}
      />

      <div style={{ position: 'relative', marginTop: 24 }}>
        <Typography.Title level={4} style={{ display: 'inline-block' }}>
          Futures
        </Typography.Title>
      </div>

      <Table
        rowKey="key"
        dataSource={futuresDataSource}
        columns={futuresColumns}
        pagination={false}
        bordered
        footer={() => futuresEditButton}
      />

      {/*<EditableTable*/}
      {/*  handleSave={item => {*/}
      {/*    item.percent /= 100;*/}
      {/*    dispatch(*/}
      {/*      updateLeverageSettings({ type: item.type, percent: item.percent })*/}
      {/*    );*/}
      {/*    console.log(item);*/}
      {/*  }}*/}
      {/*  columns={columns}*/}
      {/*  dataSource={dataSource}*/}
      {/*/>*/}
    </div>
  );
};

export default LeverageSettings;
